import { ChangeDetectionStrategy, Component, input, OnInit } from '@angular/core';
import { StateComponent } from '../../../../../../../../common/components/state/state.component';
import { UtilsService } from '../../../../../../../../common/services/utils.service';
import { BettingTypeKey, IMatchFormConfig } from '@oogaday/shared';
import { IFormModel } from '../../../../../../../../common/apis/form/model/form.model';
import { OptionsProviderService } from '../../../../../../../../common/modules/form-actions/common/components/common/components/common/services/options-provider.service';
import { NamesMetadataMapping } from '../../model/model';

@Component({
  selector: 'oo-bet-result-renderer',
  templateUrl: './bet-result-renderer.component.html',
  styleUrls: ['./bet-result-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class BetResultRendererComponent extends StateComponent<BetResultRendererComponentState> implements OnInit {


  readonly metadata = input.required<NamesMetadataMapping>();
  readonly bettingType = input.required<BettingTypeKey>();
  readonly bettingValue = input.required<string | number | number[] | {
    [key: string]: any | string[][];
  } | string[] | number[][]>();
  readonly form = input.required<IFormModel>();

  state: BetResultRendererComponentState = {
    displayVal: '',
  };


  // eslint-disable-next-line complexity
  ngOnInit(): void {
    const bettingValue = this.bettingValue();
    if (bettingValue === null || bettingValue === undefined) {
      this.updateState({ displayVal: 'No Betting' });

      return;
    }

    switch (this.bettingType()) {
      case 'winner':
        this.updateState({ displayVal: bettingValue === 'tie' ? 'Tie' : this.getItemName('teams', bettingValue as number) });
        break;
      case 'firstTeamToScore':
      case 'firstTeamToGetCard':
      case 'firstTeamToHaveCorner':
      case 'firstPlace':
      case 'lastPlace':
        this.updateState({ displayVal: bettingValue === 'none' ? 'None' : this.getItemName('teams', bettingValue as number) });
        break;
      case 'underOver':
      case 'totalGoals':
        this.updateState({ displayVal: UtilsService.camelToText(bettingValue as string, true) });
        break;
      case 'exactScore': {
        const config = this.form().config as IMatchFormConfig;
        const [homeScore, awayScore] = (bettingValue as [number, number]);
        const displayVal =
          `${this.getItemName('teams', config.homeTeamId)} (${homeScore} - ${awayScore}) ${this.getItemName('teams', config.awayTeamId)}`;
        this.updateState({ displayVal });
        break;
      }
      case 'bestGoalScorer':
        if (typeof bettingValue === 'number') {
          this.updateState({ displayVal: this.getItemName('players', bettingValue) });
        } else {
          this.updateState({
            displayVal: (bettingValue as number[]).map((playerId) => this.getItemName('players', playerId))
              .join(' Or '),
          });
        }
        break;
      case 'teamsToReachFinals':
      case 'worstTeam':
      case 'mostScoringTeam':
      case 'leastScoringTeam':
      case 'extraQualifiers':
      case 'teamToConceiveMostGoals':
      case 'teamToConceiveLeastGoals':
        this.updateState({
          displayVal: Array.isArray(bettingValue) ?
            (bettingValue as number[]).map((teamId) => this.getItemName('teams', teamId)).join(', ')
            : this.getItemName('teams', bettingValue as number),
        });
        break;
      case 'qualifiers':
      case 'notQualified': {
        const equalValue = bettingValue as (number[] | number[][]);

        if (Array.isArray(equalValue[0])) {
          this.updateState({
            displayVal: (bettingValue as number[][])
              .map((group) => group.map((teamId) => this.getItemName('teams', teamId)).join(', '))
              .map((group) => `<div>${group}</div>`)
              .join(''),
          });
        } else {
          this.updateState({
            displayVal: (equalValue as number[])
              .map((teamId) => this.getItemName('teams', teamId))
              .join(', '),
          });
        }

        break;
      }
      case 'groupsWithMostScoredGoals':
      case 'groupsWithLeastScoredGoals': {
        const value = Array.isArray(bettingValue) ?
          (bettingValue as number[]).map((val) => `Group ${UtilsService.numToLetter(val)}`)
            .join(', ')
          : `Group ${UtilsService.numToLetter(bettingValue as number)}`;
        this.updateState({ displayVal: value });
        break;
      }
      case 'goalsDiff': {
        const selectedOption = bettingValue as number;
        const { homeTeamId, awayTeamId } = (this.form().config as IMatchFormConfig);
        const diffs = OptionsProviderService
          .getGoalDiffOptions(this.getItemName('teams', homeTeamId), this.getItemName('teams', awayTeamId))[0];
        this.updateState({ displayVal: diffs.options.find((opt) => opt.value === selectedOption)!.name });
        break;
      }
      default:
        if (typeof bettingValue === 'number' || typeof bettingValue === 'string') {
          this.updateState({ displayVal: bettingValue });
        } else {
          this.updateState({ displayVal: JSON.stringify(bettingValue) });
        }
    }
  }

  private getItemName(type: keyof NamesMetadataMapping, itemId: number): string {
    return this.metadata()[type].get(itemId)!;
  }
}

interface BetResultRendererComponentState {
  displayVal: string | number;
}
